/**
 * Matching Page
 * Match bank payments to scheduled amortization payments
 * Also manages receipts for capital received from investors
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Typography,
  InputAdornment,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  CheckCircle as MatchIcon,
  Refresh as RefreshIcon,
  Receipt as ReceiptIcon,
  CloudUpload as CloudUploadIcon,
  Download as DownloadIcon,
  Description as DescriptionIcon,
  LinkOff as UnmatchIcon,
  Edit as EditIcon,
  Visibility as PreviewIcon,
  AccountBalance as AccountBalanceIcon,
  Payment as PaymentIcon,
  Merge as MergeIcon,
  SwapVert as SwapVertIcon,
  ReceiptLong as ReceiptLongIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useForm, Controller } from 'react-hook-form';
import { apiGet, apiPost, apiPut, apiUpload, apiDelete } from '../utils/apiClient';
import { formatNumber } from '../utils/validation';
import { formatFileSize } from '../utils/formatters';
import TabPanel from '../components/common/TabPanel';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { OverduePaymentsTab, UpcomingPaymentsTab } from '../components/payments';
import MatchDialog from '../components/payments/MatchDialog';
import StatCard from '../components/common/StatCard';
import { useTenant } from '../contexts/TenantContext';
import { useNotification } from '../contexts/NotificationContext';
import useDocumentActions from '../hooks/useDocumentActions';
import DocumentPreviewDialog from '../components/documents/DocumentPreviewDialog';
import ContractReferenceCell from '../components/contracts/ContractReferenceCell';
import SortableHeaderCell from '../components/common/SortableHeaderCell';
import useTableSort from '../hooks/useTableSort';

const MATCHED_PAYMENT_COLUMNS = [
  { key: 'contract',        align: 'left',  getValue: (p) => p.contract_reference },
  { key: 'investor',        align: 'left',  getValue: (p) => p.investor_name },
  { key: 'payment_date',    align: 'left',  getValue: (p) => p.payment_date },
  { key: 'amount',          align: 'right', getValue: (p) => p.payment_amount != null ? parseFloat(p.payment_amount) : null },
  { key: 'matched_date',    align: 'left',  getValue: (p) => p.processed_at },
  { key: 'reference',       align: 'left',  getValue: (p) => p.payment_reference },
  { key: 'status',          align: 'left',  getValue: (p) => p.payment_status },
];

const PENDING_DRAWDOWN_COLUMNS = [
  { key: 'contract',       align: 'left',  getValue: (d) => d.contract_reference },
  { key: 'investor',       align: 'left',  getValue: (d) => d.investor_name },
  { key: 'expected_date',  align: 'left',  getValue: (d) => d.expected_date },
  { key: 'capital_amount', align: 'right', getValue: (d) => d.capital_amount != null ? parseFloat(d.capital_amount) : null },
  { key: 'days_overdue',   align: 'left',  getValue: (d) => d.days_overdue != null ? Number(d.days_overdue) : 0 },
];

const RECEIPT_COLUMNS = [
  { key: 'contract',     align: 'left',  getValue: (r) => r.contract_reference },
  { key: 'investor',     align: 'left',  getValue: (r) => r.investor_name },
  { key: 'payment_date', align: 'left',  getValue: (r) => r.payment_date },
  { key: 'amount',       align: 'right', getValue: (r) => r.payment_amount != null ? parseFloat(r.payment_amount) : null },
  { key: 'matched_date', align: 'left',  getValue: (r) => r.processed_at },
  { key: 'reference',    align: 'left',  getValue: (r) => r.payment_reference },
  { key: 'status',       align: 'left',  getValue: (r) => r.payment_status },
];

const PENDING_WHT_COLUMNS = [
  { key: 'contract',       align: 'left',  getValue: (e) => e.contract_reference },
  { key: 'investor',       align: 'left',  getValue: (e) => e.investor_name },
  { key: 'interest_date',  align: 'left',  getValue: (e) => e.payment_date },
  { key: 'gross_interest', align: 'right', getValue: (e) => e.gross_interest_amount != null ? parseFloat(e.gross_interest_amount) : null },
  { key: 'wht_rate',       align: 'right', getValue: (e) => e.wht_rate_applied != null ? parseFloat(e.wht_rate_applied) : null },
  { key: 'wht_due',        align: 'right', getValue: (e) => e.wht_amount != null ? parseFloat(e.wht_amount) : null },
  { key: 'days_overdue',   align: 'left',  getValue: (e) => e.days_overdue != null ? Number(e.days_overdue) : 0 },
];

const MATCHED_WHT_COLUMNS = [
  { key: 'contract',     align: 'left',  getValue: (p) => p.contract_reference },
  { key: 'investor',     align: 'left',  getValue: (p) => p.investor_name },
  { key: 'payment_date', align: 'left',  getValue: (p) => p.payment_date },
  { key: 'amount',       align: 'right', getValue: (p) => p.payment_amount != null ? parseFloat(p.payment_amount) : null },
  { key: 'matched_date', align: 'left',  getValue: (p) => p.processed_at },
  { key: 'reference',    align: 'left',  getValue: (p) => p.payment_reference },
  { key: 'status',       align: 'left',  getValue: (p) => p.payment_status },
];

function PaymentMatching() {
  const { isFeatureEnabled, isWorkflowDocumentRequired } = useTenant();
  const notify = useNotification();
  const [tabValue, setTabValue] = useState(0);
  const [paymentsSubTab, setPaymentsSubTab] = useState(0);
  const [capitalSubTab, setCapitalSubTab] = useState(0);
  const matchedPaymentsSort = useTableSort();
  const pendingDrawDownsSort = useTableSort();
  const receiptsSort = useTableSort();
  const pendingWhtSort = useTableSort();
  const matchedWhtSort = useTableSort();
  const [loading, setLoading] = useState(false);
  // Distinct from `loading`: flips true once the payment feeds have loaded at
  // least once. `loading` starts false, so it's an unreliable "data ready"
  // signal for the scheduleId deeplink effect (which would otherwise search
  // empty feeds on cold mount and wrongly conclude "not found").
  const [dataLoaded, setDataLoaded] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Unified MatchDialog state. matchType picks the endpoint and which sections
  // the dialog renders (Combined gets apportionment; variance section auto-
  // unfolds for any match where cash differs from expected).
  const [matchDialogOpenUnified, setMatchDialogOpenUnified] = useState(false);
  const [matchDialogType, setMatchDialogType] = useState('payment');
  const [matchDialogRow, setMatchDialogRow] = useState(null);
  const [matchDialogSubmitting, setMatchDialogSubmitting] = useState(false);
  // Reapportion mode state — set when opening the dialog to edit an existing
  // match. Cleared on close so the next 'Match' click starts fresh.
  const [matchDialogMode, setMatchDialogMode] = useState('match'); // 'match' | 'reapportion'
  const [matchDialogPaymentId, setMatchDialogPaymentId] = useState(null);
  const [matchDialogInitialPayment, setMatchDialogInitialPayment] = useState(null);
  const [matchDialogInitialAllocation, setMatchDialogInitialAllocation] = useState(null);
  const [matchDialogInitialReason, setMatchDialogInitialReason] = useState('');

  // Data states
  const [upcomingPayments, setUpcomingPayments] = useState([]);
  const [overduePayments, setOverduePayments] = useState([]);
  const [matchedPayments, setMatchedPayments] = useState([]);

  // Combined Payments tab state (annuity / combine_payment_rows contracts)
  const [combinedOverdue, setCombinedOverdue] = useState([]);
  const [combinedUpcoming, setCombinedUpcoming] = useState([]);
  const [combinedSubTab, setCombinedSubTab] = useState(0);

  // Global contract search. Filters every visible row across every tab by
  // case-insensitive token match on contract_reference / user_reference /
  // investor_name. Pure client-side filter of rows already loaded.
  const [matchingSearch, setMatchingSearch] = useState('');

  // Capital tab — direction switch (in / out). 'in' shows the existing
  // drawdown receipts flow; 'out' shows outgoing Capital Payment / Adhoc
  // Capital Withdrawal rows (Phase 9). Out → uses the same MatchDialog as
  // the Payments tab (matchType='payment') and the same sp_payment_match_inline.
  const [capitalDirection, setCapitalDirection] = useState('in');
  const [capitalOutOverdue, setCapitalOutOverdue] = useState([]);
  const [capitalOutUpcoming, setCapitalOutUpcoming] = useState([]);
  const [capitalOutSubTab, setCapitalOutSubTab] = useState(0);

  // Receipts tab state
  const [receipts, setReceipts] = useState([]);
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);
  const [receiptFile, setReceiptFile] = useState(null);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [pendingDrawDowns, setPendingDrawDowns] = useState([]); // Pending capital draw downs from amortization schedule
  const [receiptPage, setReceiptPage] = useState(0);
  const [pendingDrawDownPage, setPendingDrawDownPage] = useState(0);
  const [selectedDrawDown, setSelectedDrawDown] = useState(null); // For receipting a specific draw down

  // WHT tab state
  const [whtSubTab, setWhtSubTab] = useState(0);
  const [pendingWht, setPendingWht] = useState([]);
  const [matchedWht, setMatchedWht] = useState([]);
  const [whtPage, setWhtPage] = useState(0);
  const [matchedWhtPage, setMatchedWhtPage] = useState(0);

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });

  // Document preview & download (shared hook)
  const docActions = useDocumentActions();

  const { control: receiptControl, handleSubmit: handleReceiptSubmit, reset: resetReceipt } = useForm({
    defaultValues: {
      contract_id: null,
      amount: '',
      received_date: format(new Date(), 'yyyy-MM-dd'),
      reference: '',
      payment_method: 'EFT',
      notes: '',
    },
  });


  useEffect(() => {
    loadAllPaymentData();
  }, []);

  const loadAllPaymentData = async () => {
    try {
      setLoading(true);
      const [upcoming, overdue, matched, receiptsData, pendingDrawDownsData, pendingWhtData, matchedWhtData, combinedOverdueData, combinedUpcomingData, capitalOutOverdueData, capitalOutUpcomingData] = await Promise.all([
        apiGet('/payments/upcoming').catch(() => ({ data: [] })),
        apiGet('/payments/overdue').catch(() => ({ data: [] })),
        apiGet('/payments?status=Matched&limit=100').catch(() => ({ data: [] })),
        apiGet('/payments/receipts').catch(() => ({ data: [] })),
        // Get pending capital draw downs from amortization schedule (not yet receipted)
        apiGet('/payments/receipts/pending').catch(() => ({ data: [] })),
        apiGet('/payments/wht/pending').catch(() => ({ data: [] })),
        apiGet('/payments/wht/matched').catch(() => ({ data: [] })),
        apiGet('/payments/combined/overdue').catch(() => ({ data: [] })),
        apiGet('/payments/combined/upcoming').catch(() => ({ data: [] })),
        apiGet('/payments/capital-out/overdue').catch(() => ({ data: [] })),
        apiGet('/payments/capital-out/upcoming').catch(() => ({ data: [] })),
      ]);

      setUpcomingPayments(Array.isArray(upcoming.data) ? upcoming.data : []);
      setOverduePayments(Array.isArray(overdue.data) ? overdue.data : []);
      setMatchedPayments(Array.isArray(matched.data) ? matched.data.filter(p => p.payment_type !== 'WHT') : []);
      // Normalise Capital Receipt field names so they line up with the rest of
      // the matching tables (payment_date / processed_at / payment_amount /
      // currency_code / payment_reference). The receipt SP returns the native
      // receipt-side names; this shim keeps it untouched.
      const normaliseReceipt = (r) => ({
        ...r,
        payment_date: r.payment_date || r.received_date,
        processed_at: r.processed_at || r.created_at,
        payment_amount: r.payment_amount != null ? r.payment_amount : r.amount,
        payment_reference: r.payment_reference || r.reference,
        currency_code: r.currency_code || r.currency,
        proof_of_payment_document: r.proof_of_payment_document || r.proof_document_id,
      });
      setReceipts(Array.isArray(receiptsData.data) ? receiptsData.data.map(normaliseReceipt) : []);
      setPendingDrawDowns(Array.isArray(pendingDrawDownsData.data) ? pendingDrawDownsData.data : []);
      setPendingWht(Array.isArray(pendingWhtData.data) ? pendingWhtData.data : []);
      setMatchedWht(Array.isArray(matchedWhtData.data) ? matchedWhtData.data : []);
      setCombinedOverdue(Array.isArray(combinedOverdueData.data) ? combinedOverdueData.data : []);
      setCombinedUpcoming(Array.isArray(combinedUpcomingData.data) ? combinedUpcomingData.data : []);
      setCapitalOutOverdue(Array.isArray(capitalOutOverdueData.data) ? capitalOutOverdueData.data : []);
      setCapitalOutUpcoming(Array.isArray(capitalOutUpcomingData.data) ? capitalOutUpcomingData.data : []);
    } catch (err) {
      console.error('Error loading payment data:', err);
    } finally {
      setLoading(false);
      setDataLoaded(true);
    }
  };

  // When a draw down is selected for receipt (from the pending table), open dialog with pre-filled data
  const handleReceiptDrawDown = (drawDown) => {
    setSelectedDrawDown(drawDown);
    setReceiptFile(null);
    resetReceipt({
      contract_id: drawDown.contract_id,
      amount: drawDown.capital_amount || '',
      received_date: format(new Date(), 'yyyy-MM-dd'),
      reference: '',
      payment_method: 'EFT',
      notes: '',
    });
    setReceiptDialogOpen(true);
  };

  // Determine matchType from the schedule row's narration. Combined Payment
  // rows always get the Combined dialog; everything else uses the standard
  // Payments dialog. WHT has its own dedicated opener (handleOpenWhtMatchDialog).
  const matchTypeForRow = (schedule) => {
    const narration = (schedule?.narration || '').trim();
    if (narration === 'Combined Payment') return 'combined';
    return 'payment';
  };

  const handleOpenMatchDialog = (schedule) => {
    setMatchDialogRow(schedule);
    setMatchDialogType(matchTypeForRow(schedule));
    setMatchDialogOpenUnified(true);
  };

  const handleCloseUnifiedMatch = () => {
    if (matchDialogSubmitting) return;
    setMatchDialogOpenUnified(false);
    setMatchDialogRow(null);
    setMatchDialogMode('match');
    setMatchDialogPaymentId(null);
    setMatchDialogInitialPayment(null);
    setMatchDialogInitialAllocation(null);
    setMatchDialogInitialReason('');
  };

  // Open MatchDialog in read-only view mode for any matched row. Works
  // across Payments / Combined / WHT / Capital Receipts. Capital receipts
  // don't have a schedule_id we can resolve to expected interest/capital,
  // so we just render the metadata we already have on the receipt row.
  const handleOpenViewDialog = async (matched, viewMatchType = 'payment') => {
    try {
      let scheduleRow = null;
      if (matched.contract_id && matched.schedule_id && viewMatchType !== 'capital-receipt') {
        const scheduleResp = await apiGet(`/amortization/contract/${matched.contract_id}`).catch(() => null);
        const scheduleRows = scheduleResp?.data?.schedule || scheduleResp?.data || [];
        const entry = Array.isArray(scheduleRows)
          ? scheduleRows.find(r => r.id === matched.schedule_id)
          : null;
        if (entry) {
          scheduleRow = {
            id: entry.id,
            contract_id: matched.contract_id,
            contract_reference: matched.contract_reference,
            user_reference: matched.user_reference,
            investor_name: matched.investor_name,
            payment_date: entry.payment_date,
            scheduled_amount: entry.payment_amount,
            payment_amount: entry.payment_amount,
            interest_amount: entry.interest_amount,
            capital_amount: entry.capital_amount,
            currency: matched.currency_code,
            narration: entry.narration,
          };
        }
      }
      if (!scheduleRow) {
        // Capital receipts (or any row where we couldn't resolve the schedule)
        // — render whatever we have, no apportionment context.
        scheduleRow = {
          id: matched.schedule_id || null,
          contract_id: matched.contract_id,
          contract_reference: matched.contract_reference,
          user_reference: matched.user_reference,
          investor_name: matched.investor_name,
          payment_date: matched.payment_date,
          scheduled_amount: matched.payment_amount,
          payment_amount: matched.payment_amount,
          interest_amount: 0,
          capital_amount: 0,
          currency: matched.currency_code,
          narration: viewMatchType === 'capital-receipt' ? 'Capital Receipt' : '',
        };
      }
      setMatchDialogRow(scheduleRow);
      setMatchDialogType(viewMatchType === 'capital-receipt' ? 'payment' : viewMatchType);
      setMatchDialogMode('view');
      setMatchDialogPaymentId(matched.id);
      setMatchDialogInitialPayment({
        payment_amount: matched.payment_amount,
        payment_date: matched.payment_date,
        payment_reference: matched.payment_reference,
        payment_method: matched.payment_method,
        notes: matched.notes,
      });
      setMatchDialogInitialAllocation({
        allocated_interest: matched.allocated_interest != null ? matched.allocated_interest : 0,
        allocated_capital: matched.allocated_capital != null ? matched.allocated_capital : 0,
      });
      setMatchDialogInitialReason(matched.variance_reason || '');
      setMatchDialogOpenUnified(true);
    } catch (err) {
      console.error('Error opening view dialog:', err);
    }
  };

  // Open MatchDialog in reapportion mode for a matched payment row.
  // Fetches the schedule entry for the row's contract so we have the expected
  // interest/capital values for the dialog header + quick-allocate buttons.
  const handleOpenReapportionDialog = async (matchedPayment) => {
    try {
      const scheduleResp = await apiGet(`/amortization/contract/${matchedPayment.contract_id}`).catch(() => null);
      const scheduleRows = scheduleResp?.data?.schedule || scheduleResp?.data || [];
      const entry = Array.isArray(scheduleRows)
        ? scheduleRows.find(r => r.id === matchedPayment.schedule_id)
        : null;

      if (!entry) {
        alert('Could not load the schedule entry for this match.');
        return;
      }

      setMatchDialogRow({
        id: entry.id,
        contract_id: matchedPayment.contract_id,
        contract_reference: matchedPayment.contract_reference,
        user_reference: matchedPayment.user_reference,
        investor_name: matchedPayment.investor_name,
        payment_date: entry.payment_date,
        scheduled_amount: entry.payment_amount,
        payment_amount: entry.payment_amount,
        interest_amount: entry.interest_amount,
        capital_amount: entry.capital_amount,
        currency: matchedPayment.currency_code,
        narration: entry.narration,
      });
      setMatchDialogType(entry.narration === 'Combined Payment' ? 'combined' : 'payment');
      setMatchDialogMode('reapportion');
      setMatchDialogPaymentId(matchedPayment.id);
      setMatchDialogInitialPayment({
        payment_amount: matchedPayment.payment_amount,
        payment_date: matchedPayment.payment_date,
        payment_reference: matchedPayment.payment_reference,
        payment_method: matchedPayment.payment_method,
        notes: matchedPayment.notes,
      });
      setMatchDialogInitialAllocation({
        allocated_interest: matchedPayment.allocated_interest != null
          ? matchedPayment.allocated_interest
          : Math.min(parseFloat(matchedPayment.payment_amount) || 0, parseFloat(entry.interest_amount) || 0),
        allocated_capital: matchedPayment.allocated_capital != null
          ? matchedPayment.allocated_capital
          : Math.max(0, (parseFloat(matchedPayment.payment_amount) || 0) - Math.min(parseFloat(matchedPayment.payment_amount) || 0, parseFloat(entry.interest_amount) || 0)),
      });
      setMatchDialogInitialReason(matchedPayment.variance_reason || '');
      setMatchDialogOpenUnified(true);
    } catch (err) {
      console.error('Error opening reapportion dialog:', err);
      alert('Failed to load match for editing');
    }
  };

  // Unified MatchDialog submit handler. Receives a structured payload:
  //   { payment: {...}, allocation?: {...}, variance?: {...} }
  // and routes to the right endpoint based on matchDialogType / matchDialogMode.
  const handleUnifiedMatchSubmit = async ({ payment, allocation, variance }) => {
    if (!matchDialogRow) return;
    try {
      setMatchDialogSubmitting(true);

      // Reapportion mode: only allocation + variance reason are mutable.
      // Hits PUT /payments/:id/apportionment instead of POST /payments/match.
      if (matchDialogMode === 'reapportion' && matchDialogPaymentId) {
        // For Combined rows the dialog supplies an allocation block. For
        // single-bucket rows the SP can derive defaults — but we still need
        // to send concrete values that sum to payment_amount. Fall back to
        // the existing initialAllocation for non-Combined paths.
        const alloc = allocation || matchDialogInitialAllocation || {};
        await apiPut(`/payments/${matchDialogPaymentId}/apportionment`, {
          allocated_interest: alloc.allocated_interest,
          allocated_capital: alloc.allocated_capital,
          variance_reason: variance?.reason || null,
        });
        handleCloseUnifiedMatch();
        await loadAllPaymentData();
        setMatchDialogSubmitting(false);
        return;
      }

      // Per-tenant workflow requirement: refuse to match without proof when
      // the workflow has is_required = true.
      const workflowCode = matchDialogType === 'wht' ? 'wht_match' : 'payment_match';
      if (!payment.proof_file && isWorkflowDocumentRequired(workflowCode)) {
        notify.error('A proof of payment document is required to match this payment.');
        setMatchDialogSubmitting(false);
        return;
      }

      // Upload proof first (best-effort; non-blocking on failure to keep
      // existing behaviour).
      let proofDocumentId = null;
      if (payment.proof_file) {
        const formData = new FormData();
        formData.append('file', payment.proof_file);
        formData.append('document_type', matchDialogType === 'wht' ? 'proof_of_wht_payment' : 'proof_of_payment');
        formData.append('contract_id', matchDialogRow.contract_id);
        formData.append('notes', `Proof of payment for ${matchDialogRow.contract_reference || ''} - ${payment.payment_reference || 'No reference'}`);
        try {
          const uploadResult = await apiUpload('/admin/documents/upload', formData);
          proofDocumentId = uploadResult.data?.id;
        } catch (uploadErr) {
          console.error('Error uploading proof:', uploadErr);
        }
      }

      const endpoint = matchDialogType === 'wht' ? '/payments/wht/match' : '/payments/match';
      const payload = {
        schedule_id: matchDialogRow.id,
        payment_amount: payment.payment_amount,
        payment_date: payment.payment_date,
        payment_reference: payment.payment_reference,
        payment_method: payment.payment_method,
        notes: payment.notes,
        proof_document_id: proofDocumentId,
      };

      if (matchDialogType === 'combined' && allocation) {
        payload.allocated_interest = allocation.allocated_interest;
        payload.allocated_capital = allocation.allocated_capital;
      }
      if (variance?.reason) {
        payload.variance_reason = variance.reason;
      }

      await apiPost(endpoint, payload);
      setMatchDialogOpenUnified(false);
      setMatchDialogRow(null);
      await loadAllPaymentData();
    } catch (err) {
      console.error('Error matching payment:', err);
      const serverMessage = err.response?.data?.error?.message || err.response?.data?.message || err.message;
      alert('Failed to match payment' + (serverMessage ? `: ${serverMessage}` : ''));
    } finally {
      setMatchDialogSubmitting(false);
    }
  };

  const handleUnmatchPayment = (paymentId) => {
    setConfirmDialog({
      open: true,
      title: 'Unmatch Payment',
      message: 'Are you sure you want to unmatch this payment? This will revert it to Pending status.',
      onConfirm: async () => {
        try {
          await apiPost(`/payments/${paymentId}/unmatch`);
          setConfirmDialog(prev => ({ ...prev, open: false }));
          await loadAllPaymentData();
        } catch (err) {
          console.error('Error unmatching payment:', err);
          setConfirmDialog(prev => ({ ...prev, open: false }));
          alert('Failed to unmatch payment: ' + (err.response?.data?.message || err.message));
        }
      },
    });
  };

  // Receipt handlers
  const handleCloseReceiptDialog = () => {
    setReceiptDialogOpen(false);
    setReceiptFile(null);
    setSelectedDrawDown(null);
    resetReceipt();
  };

  const handleCreateReceipt = async (data) => {
    // Get the contract/investor info from selectedDrawDown or from manual entry
    const contractId = selectedDrawDown?.contract_id || data.contract_id;
    const investorId = selectedDrawDown?.investor_id || data.investor_id;
    const contractRef = selectedDrawDown?.contract_reference || 'Manual';

    if (!investorId || !contractId) {
      alert('Please select a contract');
      return;
    }

    try {
      setUploadingReceipt(true);

      if (!receiptFile && isWorkflowDocumentRequired('capital_receipt')) {
        notify.error('A proof of receipt document is required to record a capital receipt.');
        setUploadingReceipt(false);
        return;
      }

      // Upload proof document first if provided
      let proofDocumentId = null;
      if (receiptFile) {
        const formData = new FormData();
        formData.append('file', receiptFile);
        formData.append('document_type', 'proof_of_receipt');
        formData.append('investor_id', investorId);
        formData.append('contract_id', contractId);
        formData.append('notes', `Capital receipt for ${contractRef} - ${data.reference || 'No reference'}`);

        try {
          const uploadResult = await apiUpload('/admin/documents/upload', formData);
          proofDocumentId = uploadResult.data?.id;
        } catch (uploadErr) {
          console.error('Error uploading receipt proof:', uploadErr);
        }
      }

      const payload = {
        investor_id: investorId,
        contract_id: contractId,
        schedule_id: selectedDrawDown?.schedule_id || null,
        amount: parseFloat(data.amount),
        received_date: data.received_date,
        reference: data.reference,
        payment_method: data.payment_method,
        notes: data.notes,
        proof_document_id: proofDocumentId,
      };

      await apiPost('/payments/receipts', payload);
      await loadAllPaymentData();
      handleCloseReceiptDialog();
    } catch (err) {
      console.error('Error creating receipt:', err);
      alert('Failed to create receipt: ' + (err.message || 'Unknown error'));
    } finally {
      setUploadingReceipt(false);
    }
  };

  const handleDeleteReceipt = (receiptId) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Receipt',
      message: 'Are you sure you want to delete this receipt?',
      onConfirm: async () => {
        try {
          await apiDelete(`/payments/receipts/${receiptId}`);
          setConfirmDialog(prev => ({ ...prev, open: false }));
          await loadAllPaymentData();
        } catch (err) {
          console.error('Error deleting receipt:', err);
          setConfirmDialog(prev => ({ ...prev, open: false }));
          alert('Failed to delete receipt');
        }
      },
    });
  };

  // WHT handlers
  const handleOpenWhtMatchDialog = (whtEntry) => {
    // Adapt the WHT entry shape to the schedule-row shape MatchDialog expects.
    // The WHT match SP works against the same schedule_id, with the WHT amount
    // as the expected value.
    const adapted = {
      id: whtEntry.schedule_id,
      contract_id: whtEntry.contract_id,
      contract_reference: whtEntry.contract_reference,
      user_reference: whtEntry.user_reference,
      investor_name: whtEntry.investor_name,
      payment_date: whtEntry.payment_date,
      scheduled_amount: whtEntry.wht_amount,
      payment_amount: whtEntry.wht_amount,
      interest_amount: 0,
      capital_amount: 0,
      currency: whtEntry.currency_code,
      narration: 'Withholding Tax',
      _wht_context: {
        gross_interest: whtEntry.gross_interest_amount,
        wht_rate: whtEntry.wht_rate_applied,
        wht_amount: whtEntry.wht_amount,
      },
    };
    setMatchDialogRow(adapted);
    setMatchDialogType('wht');
    setMatchDialogOpenUnified(true);
  };

  const handleUnmatchWht = (paymentId) => {
    setConfirmDialog({
      open: true,
      title: 'Unmatch WHT Payment',
      message: 'Are you sure you want to unmatch this WHT payment?',
      onConfirm: async () => {
        try {
          await apiPost(`/payments/${paymentId}/unmatch`);
          setConfirmDialog(prev => ({ ...prev, open: false }));
          await loadAllPaymentData();
        } catch (err) {
          console.error('Error unmatching WHT payment:', err);
          setConfirmDialog(prev => ({ ...prev, open: false }));
          alert('Failed to unmatch WHT payment: ' + (err.response?.data?.message || err.message));
        }
      },
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'success';
      case 'overdue':
        return 'error';
      case 'partial':
        return 'warning';
      case 'pending':
        return 'default';
      default:
        return 'default';
    }
  };

  // Sub-feature tab filtering. 'combined' shares the 'matching' feature gate —
  // annuity contracts produce Combined Payment rows that belong to the same
  // payments workflow as Interest Payment rows.
  const allPaymentTabs = useMemo(() => [
    { feature: 'matching', label: 'Payments', key: 'matching' },
    { feature: 'matching', label: 'Combined', key: 'combined' },
    { feature: 'capital_receipts', label: 'Capital', key: 'capital_receipts' },
    { feature: 'withholding_tax', label: 'Withholding Tax', key: 'withholding_tax' },
  ], []);

  const visiblePaymentTabs = useMemo(() =>
    allPaymentTabs.filter(t => isFeatureEnabled('payments', t.feature)),
    [allPaymentTabs, isFeatureEnabled]
  );

  const safeTabValue = visiblePaymentTabs.length > 0 ? Math.min(tabValue, visiblePaymentTabs.length - 1) : 0;
  const activePaymentFeature = visiblePaymentTabs[safeTabValue]?.key || visiblePaymentTabs[safeTabValue]?.feature;
  // Alias used by the summary StatCards.
  const paymentsTabKey = activePaymentFeature;

  // Track per-contract expansion state across the four Matched tabs. Default:
  // collapsed unless the contract has only one or two rows (auto-expanded so
  // small accounts stay visible). The user can toggle individually.
  const [groupExpanded, setGroupExpanded] = useState({});
  const toggleGroup = (key) => setGroupExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  // Render a group header row for a Matched table. Click toggles expand state.
  // colSpan is taken so the header spans the full table width.
  const renderGroupHeader = (group, colSpan, expanded) => (
    <TableRow
      key={`group-${group.key}`}
      hover
      onClick={(e) => { e.stopPropagation(); toggleGroup(group.key); }}
      sx={{ cursor: 'pointer', backgroundColor: '#fafafa', '&:hover': { backgroundColor: '#f0f0f0' } }}
    >
      <TableCell colSpan={colSpan} sx={{ py: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="primary" sx={{ fontWeight: 600 }}>
              {expanded ? '▾' : '▸'}
            </Typography>
            <ContractReferenceCell reference={group.contract_reference} userReference={group.user_reference} />
            <Typography variant="caption" color="textSecondary">· {group.investor_name}</Typography>
          </Box>
          <Typography variant="caption" color="textSecondary">
            {group.rows.length} matched · {group.currency_code || ''} {formatNumber(group.total_amount)}
          </Typography>
        </Box>
      </TableCell>
    </TableRow>
  );

  // Default: every group starts collapsed. User toggles individually.
  const isGroupExpanded = (group) => !!groupExpanded[group.key];

  // Group a Matched rowset by contract. Returns an array of group objects in
  // first-seen order so the table still sorts intuitively. Each group carries
  // the rolling count + total payment amount for the per-contract header.
  const groupByContract = (rows) => {
    if (!Array.isArray(rows) || rows.length === 0) return [];
    const map = new Map();
    const order = [];
    for (const r of rows) {
      const key = r.contract_reference || r.contract_id || '__unknown__';
      if (!map.has(key)) {
        order.push(key);
        map.set(key, {
          key,
          contract_reference: r.contract_reference,
          user_reference: r.user_reference,
          investor_name: r.investor_name,
          currency_code: r.currency_code,
          rows: [],
          total_amount: 0,
        });
      }
      const g = map.get(key);
      g.rows.push(r);
      g.total_amount += parseFloat(r.payment_amount) || 0;
    }
    return order.map((k) => map.get(k));
  };

  // Token-AND filter on contract_reference / user_reference / investor_name.
  // Empty search returns the input array unchanged.
  const applySearch = useMemo(() => {
    const raw = (matchingSearch || '').trim().toLowerCase();
    if (!raw) return (rows) => rows;
    const tokens = raw.split(/\s+/).filter(Boolean);
    return (rows) => (Array.isArray(rows) ? rows.filter((r) => {
      const hay = `${r.contract_reference || ''} ${r.user_reference || ''} ${r.investor_name || ''}`.toLowerCase();
      return tokens.every((t) => hay.includes(t));
    }) : []);
  }, [matchingSearch]);

  // Filtered views used by every Matched / Overdue / Upcoming table.
  const fOverdue = useMemo(() => applySearch(overduePayments), [applySearch, overduePayments]);
  const fUpcoming = useMemo(() => applySearch(upcomingPayments), [applySearch, upcomingPayments]);
  const fMatched = useMemo(() => applySearch(matchedPayments), [applySearch, matchedPayments]);
  const fCombinedOverdue = useMemo(() => applySearch(combinedOverdue), [applySearch, combinedOverdue]);
  const fCombinedUpcoming = useMemo(() => applySearch(combinedUpcoming), [applySearch, combinedUpcoming]);
  const fCapitalOutOverdue = useMemo(() => applySearch(capitalOutOverdue), [applySearch, capitalOutOverdue]);
  const fCapitalOutUpcoming = useMemo(() => applySearch(capitalOutUpcoming), [applySearch, capitalOutUpcoming]);
  const fPendingDrawDowns = useMemo(() => applySearch(pendingDrawDowns), [applySearch, pendingDrawDowns]);
  const fReceipts = useMemo(() => applySearch(receipts), [applySearch, receipts]);
  const fPendingWht = useMemo(() => applySearch(pendingWht), [applySearch, pendingWht]);
  const fMatchedWht = useMemo(() => applySearch(matchedWht), [applySearch, matchedWht]);
  const switchToPaymentTab = (key) => {
    const idx = visiblePaymentTabs.findIndex(t => (t.key || t.feature) === key);
    if (idx >= 0) setTabValue(idx);
  };

  // Deeplink support: /payments?tab=matching&contractRef=ABC pre-selects the tab
  // and pre-populates the contract search. One-shot per mount — once the dashboard
  // (or any other source) sends a user here, we honour the hint then get out of the way.
  const [searchParams] = useSearchParams();
  const deeplinkApplied = useRef(false);
  useEffect(() => {
    if (deeplinkApplied.current) return;
    if (!visiblePaymentTabs || visiblePaymentTabs.length === 0) return;
    const tab = searchParams.get('tab');
    const ref = searchParams.get('contractRef');
    if (tab) switchToPaymentTab(tab);
    if (ref) setMatchingSearch(ref);
    if (tab || ref) deeplinkApplied.current = true;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visiblePaymentTabs, searchParams]);

  // Deeplink: /payments?scheduleId=<cas.id> auto-opens the match modal for that
  // schedule row (clicked from the dashboard Upcoming Events list). The payment
  // feeds load async, so we wait for them, then find the row across all feeds,
  // switch to its tab, and open the dialog. Tracking the last-applied id (rather
  // than a one-shot boolean) lets a fresh navigation with a different id re-fire.
  const deeplinkScheduleApplied = useRef(null);
  useEffect(() => {
    const scheduleId = searchParams.get('scheduleId');
    if (!scheduleId || deeplinkScheduleApplied.current === scheduleId) return;
    // Wait until the feeds have actually loaded before deciding "not found".
    // `loading` is unreliable here (starts false), so gate on dataLoaded.
    if (!dataLoaded) return;

    // Each group ties a top tab to its overdue/upcoming sub-feeds and the
    // sub-tab setter. Sub-tab index: 0 = Overdue, 1 = Upcoming. We set it so
    // the row is visible behind the modal (dashboard events are future-dated,
    // so they almost always land on the Upcoming sub-tab).
    // The Capital tab additionally has an In/Out direction toggle (default 'in').
    // Dashboard Capital events are outgoing, so the capital group also flips
    // capitalDirection to 'out' — without it the Out section isn't rendered.
    const groups = [
      { key: 'matching',         setSub: setPaymentsSubTab,   overdue: overduePayments,    upcoming: upcomingPayments },
      { key: 'combined',         setSub: setCombinedSubTab,   overdue: combinedOverdue,    upcoming: combinedUpcoming },
      { key: 'capital_receipts', setSub: setCapitalOutSubTab, overdue: capitalOutOverdue,  upcoming: capitalOutUpcoming,
        setDirection: setCapitalDirection, direction: 'out' },
    ];

    let found = null;
    for (const g of groups) {
      const idMatch = (r) => String(r.id) === String(scheduleId);
      if (g.overdue.some(idMatch)) {
        found = { ...g, subIndex: 0, row: g.overdue.find(idMatch) };
        break;
      }
      if (g.upcoming.some(idMatch)) {
        found = { ...g, subIndex: 1, row: g.upcoming.find(idMatch) };
        break;
      }
    }

    deeplinkScheduleApplied.current = scheduleId;
    if (found) {
      switchToPaymentTab(found.key);
      if (found.setDirection) found.setDirection(found.direction);
      found.setSub(found.subIndex);
      handleOpenMatchDialog(found.row);
    } else {
      notify.info('This payment is no longer pending — it may already be matched.');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, dataLoaded, overduePayments, upcomingPayments, combinedOverdue, combinedUpcoming, capitalOutOverdue, capitalOutUpcoming]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Matching</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadAllPaymentData}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>
      {/* Summary Cards — Approvals-style StatCards with per-direction breakdowns.
          Clicking a card switches the top tab. */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <StatCard
            title="All Outflows"
            value={overduePayments.length + upcomingPayments.length + combinedOverdue.length + combinedUpcoming.length + capitalOutOverdue.length + capitalOutUpcoming.length + pendingWht.length}
            subtitle="All outstanding"
            Icon={AccountBalanceIcon}
            color="#1976d2"
            breakdown={[
              { label: 'Overdue', value: overduePayments.length + combinedOverdue.length + capitalOutOverdue.length },
              { label: 'Upcoming', value: upcomingPayments.length + combinedUpcoming.length + capitalOutUpcoming.length },
              { label: 'Withholding tax pending', value: pendingWht.length },
            ]}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <StatCard
            title="Payments"
            value={overduePayments.length + upcomingPayments.length}
            subtitle="Interest"
            Icon={PaymentIcon}
            color="#0088FE"
            selected={paymentsTabKey === 'matching'}
            onClick={() => switchToPaymentTab('matching')}
            breakdown={[
              { label: 'Overdue', value: overduePayments.length },
              { label: 'Upcoming', value: upcomingPayments.length },
              { label: 'Matched', value: matchedPayments.filter(p => p.payment_type !== 'Full Settlement').length },
            ]}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <StatCard
            title="Combined"
            value={combinedOverdue.length + combinedUpcoming.length}
            subtitle="Annuity"
            Icon={MergeIcon}
            color="#00C49F"
            selected={paymentsTabKey === 'combined'}
            onClick={() => switchToPaymentTab('combined')}
            breakdown={[
              { label: 'Overdue', value: combinedOverdue.length },
              { label: 'Upcoming', value: combinedUpcoming.length },
              { label: 'Matched', value: matchedPayments.filter(p => p.payment_type === 'Full Settlement').length },
            ]}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <StatCard
            title="Capital"
            value={pendingDrawDowns.length + capitalOutOverdue.length + capitalOutUpcoming.length}
            subtitle="In + Out"
            Icon={SwapVertIcon}
            color="#FFBB28"
            selected={paymentsTabKey === 'capital_receipts'}
            onClick={() => switchToPaymentTab('capital_receipts')}
            breakdown={[
              { label: 'Incoming pending', value: pendingDrawDowns.length },
              { label: 'Outgoing overdue', value: capitalOutOverdue.length },
              { label: 'Outgoing upcoming', value: capitalOutUpcoming.length },
            ]}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <StatCard
            title="Withholding Tax"
            value={pendingWht.length}
            subtitle="Tax remittances"
            Icon={ReceiptLongIcon}
            color="#FF8042"
            selected={paymentsTabKey === 'withholding_tax'}
            onClick={() => switchToPaymentTab('withholding_tax')}
            breakdown={[
              { label: 'Pending', value: pendingWht.length },
              { label: 'Matched', value: matchedWht.length },
            ]}
          />
        </Grid>
      </Grid>
      {/* Contract search — filters every visible row by contract ref / user ref / investor name. */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <TextField
          size="small"
          placeholder="Search by contract reference, external reference, or investor…"
          value={matchingSearch}
          onChange={(e) => setMatchingSearch(e.target.value)}
          sx={{ width: { xs: '100%', sm: 480 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <span style={{ color: '#888' }}>⌕</span>
              </InputAdornment>
            ),
            endAdornment: matchingSearch ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setMatchingSearch('')} aria-label="Clear search">
                  <span style={{ fontSize: 16 }}>×</span>
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        {matchingSearch && (
          <Typography variant="caption" color="textSecondary">
            Filtered
          </Typography>
        )}
      </Box>
      {/* Top-Level Tabs. When a search is active each tab gets a (N) suffix
          counting filtered matches under that tab, so the user can see at a
          glance which tabs have hits without having to click each one. */}
      <Paper sx={{ mb: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Tabs value={safeTabValue} onChange={(_, v) => setTabValue(v)}>
          {visiblePaymentTabs.map(t => {
            const key = t.key || t.feature;
            let count = 0;
            if (key === 'matching') count = fOverdue.length + fUpcoming.length + fMatched.filter(p => p.payment_type !== 'Full Settlement').length;
            else if (key === 'combined') count = fCombinedOverdue.length + fCombinedUpcoming.length + fMatched.filter(p => p.payment_type === 'Full Settlement').length;
            else if (key === 'capital_receipts') count = fPendingDrawDowns.length + fReceipts.length + fCapitalOutOverdue.length + fCapitalOutUpcoming.length;
            else if (key === 'withholding_tax') count = fPendingWht.length + fMatchedWht.length;
            const label = matchingSearch ? `${t.label} (${count})` : t.label;
            return <Tab key={key} label={label} />;
          })}
        </Tabs>
        {activePaymentFeature === 'capital_receipts' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 2 }}>
            <Typography variant="caption" color="textSecondary">Direction:</Typography>
            <Button
              size="small"
              variant={capitalDirection === 'in' ? 'contained' : 'outlined'}
              onClick={() => setCapitalDirection('in')}
            >
              Incoming ({fPendingDrawDowns.length + fReceipts.length})
            </Button>
            <Button
              size="small"
              variant={capitalDirection === 'out' ? 'contained' : 'outlined'}
              onClick={() => setCapitalDirection('out')}
            >
              Outgoing ({fCapitalOutOverdue.length + fCapitalOutUpcoming.length})
            </Button>
          </Box>
        )}
      </Paper>
      {/* ===== Payments Tab ===== */}
      {activePaymentFeature === 'matching' && (
      <Box>
        <Paper sx={{ mb: 0 }}>
          <Tabs value={paymentsSubTab} onChange={(_, v) => setPaymentsSubTab(v)}>
            <Tab label={`Overdue (${fOverdue.length})`} />
            <Tab label={`Upcoming (${fUpcoming.length})`} />
            <Tab label={`Matched (${fMatched.length})`} />
          </Tabs>
        </Paper>

        {/* Overdue Payments Sub-Tab */}
        <TabPanel value={paymentsSubTab} index={0}>
          <OverduePaymentsTab
            payments={fOverdue.map(p => ({
              ...p,
              payment_date: p.payment_date,
              payment_amount: p.scheduled_amount,
              currency_code: p.currency,
            }))}
            showContractColumn={true}
            showInvestorColumn={true}
            showMatchActions={true}
            onMatch={handleOpenMatchDialog}
          />
        </TabPanel>

        {/* Upcoming Payments Sub-Tab */}
        <TabPanel value={paymentsSubTab} index={1}>
          <UpcomingPaymentsTab
            payments={fUpcoming.map(p => ({
              ...p,
              payment_date: p.payment_date,
              payment_amount: p.scheduled_amount,
              currency_code: p.currency,
            }))}
            showContractColumn={true}
            showInvestorColumn={true}
            showMatchActions={true}
            onMatch={handleOpenMatchDialog}
            showFilter={false}
          />
        </TabPanel>

        {/* Matched Payments Sub-Tab */}
        <TabPanel value={paymentsSubTab} index={2}>
          <Paper>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <SortableHeaderCell sort={matchedPaymentsSort} columnKey="contract">Contract</SortableHeaderCell>
                    <SortableHeaderCell sort={matchedPaymentsSort} columnKey="investor">Investor</SortableHeaderCell>
                    <SortableHeaderCell sort={matchedPaymentsSort} columnKey="payment_date">Payment Date</SortableHeaderCell>
                    <SortableHeaderCell sort={matchedPaymentsSort} columnKey="amount" align="right">Amount</SortableHeaderCell>
                    <SortableHeaderCell sort={matchedPaymentsSort} columnKey="matched_date">Matched Date</SortableHeaderCell>
                    <SortableHeaderCell sort={matchedPaymentsSort} columnKey="reference">Reference</SortableHeaderCell>
                    <TableCell>Proof</TableCell>
                    <SortableHeaderCell sort={matchedPaymentsSort} columnKey="status">Status</SortableHeaderCell>
                    <TableCell align="right">Variance</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groupByContract(matchedPaymentsSort.sortRows(fMatched, MATCHED_PAYMENT_COLUMNS)).flatMap((group) => {
                    const expanded = isGroupExpanded(group);
                    const header = renderGroupHeader(group, 10, expanded);
                    if (!expanded) return [header];
                    return [header, ...group.rows.map((payment) => (
                    <TableRow
                      key={payment.id}
                      hover
                      onClick={() => handleOpenViewDialog(payment, 'payment')}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <ContractReferenceCell reference={payment.contract_reference} userReference={payment.user_reference} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{payment.investor_name}</Typography>
                      </TableCell>
                      <TableCell>
                        {format(new Date(payment.payment_date), 'dd MMM yyyy')}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="medium">
                          {payment.currency_code} {formatNumber(payment.payment_amount)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {payment.processed_at ? format(new Date(payment.processed_at), 'dd MMM yyyy') : '-'}
                      </TableCell>
                      <TableCell>{payment.payment_reference || '-'}</TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        {payment.proof_of_payment_document ? (
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title="Preview Document">
                              <IconButton
                                size="small"
                                onClick={() => docActions.openPreview(payment.proof_of_payment_document)}
                              >
                                <PreviewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Download Proof">
                              <IconButton
                                size="small"
                                onClick={() => docActions.downloadDocument(payment.proof_of_payment_document)}
                              >
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ) : (
                          <Typography variant="caption" color="textSecondary">-</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={payment.payment_status}
                          size="small"
                          variant="outlined"
                          color={getStatusColor(payment.payment_status)}
                        />
                      </TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        {payment.variance_amount != null && Math.abs(parseFloat(payment.variance_amount)) >= 0.01 ? (
                          <Tooltip title={payment.variance_reason || 'No reason recorded'}>
                            <Chip
                              size="small"
                              label={`${parseFloat(payment.variance_amount) < 0 ? 'Short' : 'Over'} ${payment.currency_code || ''} ${formatNumber(Math.abs(parseFloat(payment.variance_amount)))}`}
                              color={parseFloat(payment.variance_amount) < 0 ? 'error' : 'success'}
                              variant="outlined"
                            />
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="textSecondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="Edit apportionment / variance reason">
                          <IconButton size="small" color="primary" onClick={() => handleOpenReapportionDialog(payment)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Unmatch payment">
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={() => handleUnmatchPayment(payment.id)}
                          >
                            <UnmatchIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))];
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={matchedPayments.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
            />
          </Paper>
        </TabPanel>
      </Box>
      )}
      {/* ===== Combined Payments Tab (annuity / combine_payment_rows contracts) ===== */}
      {activePaymentFeature === 'combined' && (
      <Box>
        {(() => {
          // Combined matches are sp_payment_match_inline rows where the row had
          // both interest and capital — sp_payment_match_inline assigns
          // payment_type = 'Full Settlement' in that case. Filter the existing
          // matched feed; no new endpoint needed.
          const combinedMatched = fMatched.filter(p => p.payment_type === 'Full Settlement');
          return (
            <>
              <Paper sx={{ mb: 0 }}>
                <Tabs value={combinedSubTab} onChange={(_, v) => setCombinedSubTab(v)}>
                  <Tab label={`Overdue (${fCombinedOverdue.length})`} />
                  <Tab label={`Upcoming (${fCombinedUpcoming.length})`} />
                  <Tab label={`Matched (${combinedMatched.length})`} />
                </Tabs>
              </Paper>

              <TabPanel value={combinedSubTab} index={0}>
                <OverduePaymentsTab
                  payments={fCombinedOverdue.map(p => ({
                    ...p,
                    payment_date: p.payment_date,
                    payment_amount: p.scheduled_amount,
                    currency_code: p.currency,
                  }))}
                  showContractColumn={true}
                  showInvestorColumn={true}
                  showMatchActions={true}
                  onMatch={handleOpenMatchDialog}
                />
              </TabPanel>

              <TabPanel value={combinedSubTab} index={1}>
                <UpcomingPaymentsTab
                  payments={fCombinedUpcoming.map(p => ({
                    ...p,
                    payment_date: p.payment_date,
                    payment_amount: p.scheduled_amount,
                    currency_code: p.currency,
                  }))}
                  showContractColumn={true}
                  showInvestorColumn={true}
                  showMatchActions={true}
                  onMatch={handleOpenMatchDialog}
                />
              </TabPanel>

              <TabPanel value={combinedSubTab} index={2}>
                <Paper>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                          <TableCell>Contract</TableCell>
                          <TableCell>Investor</TableCell>
                          <TableCell>Payment Date</TableCell>
                          <TableCell align="right">Amount</TableCell>
                          <TableCell align="right">Allocated Interest</TableCell>
                          <TableCell align="right">Allocated Capital</TableCell>
                          <TableCell>Matched Date</TableCell>
                          <TableCell>Reference</TableCell>
                          <TableCell>Proof</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell align="right">Variance</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {combinedMatched.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={12} align="center">
                              <Typography variant="body2" color="textSecondary" sx={{ py: 3 }}>
                                No matched combined payments yet.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          groupByContract(combinedMatched).flatMap((group) => {
                          const expanded = isGroupExpanded(group);
                          const header = renderGroupHeader(group, 12, expanded);
                          if (!expanded) return [header];
                          return [header, ...group.rows.map((payment) => (
                            <TableRow
                              key={payment.id}
                              hover
                              onClick={() => handleOpenViewDialog(payment, 'combined')}
                              sx={{ cursor: 'pointer' }}
                            >
                              <TableCell>
                                <ContractReferenceCell reference={payment.contract_reference} userReference={payment.user_reference} />
                              </TableCell>
                              <TableCell><Typography variant="body2">{payment.investor_name}</Typography></TableCell>
                              <TableCell>{format(new Date(payment.payment_date), 'dd MMM yyyy')}</TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" fontWeight="medium">
                                  {payment.currency_code} {formatNumber(payment.payment_amount)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                {payment.allocated_interest != null
                                  ? `${payment.currency_code} ${formatNumber(payment.allocated_interest)}`
                                  : '—'}
                              </TableCell>
                              <TableCell align="right">
                                {payment.allocated_capital != null
                                  ? `${payment.currency_code} ${formatNumber(payment.allocated_capital)}`
                                  : '—'}
                              </TableCell>
                              <TableCell>{payment.processed_at ? format(new Date(payment.processed_at), 'dd MMM yyyy') : '-'}</TableCell>
                              <TableCell>{payment.payment_reference || '-'}</TableCell>
                              <TableCell onClick={(e) => e.stopPropagation()}>
                                {payment.proof_of_payment_document ? (
                                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    <Tooltip title="Preview Document">
                                      <IconButton size="small" onClick={() => docActions.openPreview(payment.proof_of_payment_document)}>
                                        <PreviewIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Download Proof">
                                      <IconButton size="small" onClick={() => docActions.downloadDocument(payment.proof_of_payment_document)}>
                                        <DownloadIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                ) : (
                                  <Typography variant="caption" color="textSecondary">-</Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={payment.payment_status || 'Matched'}
                                  size="small"
                                  variant="outlined"
                                  color={getStatusColor(payment.payment_status || 'matched')}
                                />
                              </TableCell>
                              <TableCell align="right">
                                {payment.variance_amount != null && Math.abs(parseFloat(payment.variance_amount)) >= 0.01 ? (
                                  <Tooltip title={payment.variance_reason || 'No reason recorded'}>
                                    <Chip
                                      size="small"
                                      label={`${parseFloat(payment.variance_amount) < 0 ? 'Short' : 'Over'} ${payment.currency_code || ''} ${formatNumber(Math.abs(parseFloat(payment.variance_amount)))}`}
                                      color={parseFloat(payment.variance_amount) < 0 ? 'error' : 'success'}
                                      variant="outlined"
                                    />
                                  </Tooltip>
                                ) : (
                                  <Typography variant="caption" color="textSecondary">—</Typography>
                                )}
                              </TableCell>
                              <TableCell align="center">
                                <Tooltip title="Edit apportionment / variance reason">
                                  <IconButton size="small" color="primary" onClick={() => handleOpenReapportionDialog(payment)}>
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Unmatch payment">
                                  <IconButton size="small" color="warning" onClick={() => handleUnmatchPayment(payment.id)}>
                                    <UnmatchIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))];
                          })
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </TabPanel>
            </>
          );
        })()}
      </Box>
      )}
      {/* ===== Capital Tab ===== */}
      {activePaymentFeature === 'capital_receipts' && (
      <Box>
        {/* Direction switch lives inline with the top-level tabs (right side
            of the Paper above). The body just renders the active direction. */}
        {capitalDirection === 'in' && (
        <>
        <Paper sx={{ mb: 0 }}>
          <Tabs value={capitalSubTab} onChange={(_, v) => setCapitalSubTab(v)}>
            <Tab label={`Pending Receipts (${fPendingDrawDowns.length})`} />
            <Tab label={`Matched Receipts (${fReceipts.length})`} />
          </Tabs>
        </Paper>

        {/* Pending Capital Receipts Sub-Tab */}
        <TabPanel value={capitalSubTab} index={0}>
          <Paper>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <SortableHeaderCell sort={pendingDrawDownsSort} columnKey="contract">Contract</SortableHeaderCell>
                    <SortableHeaderCell sort={pendingDrawDownsSort} columnKey="investor">Investor</SortableHeaderCell>
                    <SortableHeaderCell sort={pendingDrawDownsSort} columnKey="expected_date">Expected Date</SortableHeaderCell>
                    <SortableHeaderCell sort={pendingDrawDownsSort} columnKey="capital_amount" align="right">Capital Amount</SortableHeaderCell>
                    <SortableHeaderCell sort={pendingDrawDownsSort} columnKey="days_overdue">Status</SortableHeaderCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingDrawDowns.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Box sx={{ py: 3 }}>
                          <MatchIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
                          <Typography color="success.main" fontWeight="medium">
                            All capital has been received
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            No pending capital draw downs for active contracts
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    pendingDrawDownsSort.sortRows(fPendingDrawDowns, PENDING_DRAWDOWN_COLUMNS).slice(pendingDrawDownPage * rowsPerPage, pendingDrawDownPage * rowsPerPage + rowsPerPage).map((drawDown) => (
                      <TableRow
                        key={`${drawDown.contract_id}-${drawDown.schedule_id}`}
                        hover
                        onClick={() => handleReceiptDrawDown(drawDown)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          <ContractReferenceCell reference={drawDown.contract_reference} userReference={drawDown.user_reference} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{drawDown.investor_name}</Typography>
                          {drawDown.investor_email && (
                            <Typography variant="caption" color="textSecondary" display="block">
                              {drawDown.investor_email}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {format(new Date(drawDown.expected_date), 'dd MMM yyyy')}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="medium">
                            {drawDown.currency_code} {formatNumber(drawDown.capital_amount)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {drawDown.days_overdue > 0 ? (
                            <Chip
                              label={`${drawDown.days_overdue} days overdue`}
                              size="small"
                              variant="outlined"
                              color="error"
                            />
                          ) : (
                            <Chip label="Pending" size="small" variant="outlined" />
                          )}
                        </TableCell>
                        <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleReceiptDrawDown(drawDown)}
                          >
                            Receipt
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {pendingDrawDowns.length > rowsPerPage && (
              <TablePagination
                component="div"
                count={pendingDrawDowns.length}
                page={pendingDrawDownPage}
                onPageChange={(_, newPage) => setPendingDrawDownPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPendingDrawDownPage(0);
                }}
              />
            )}
          </Paper>
        </TabPanel>

        {/* Matched Capital Receipts Sub-Tab */}
        <TabPanel value={capitalSubTab} index={1}>
          <Paper>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <SortableHeaderCell sort={receiptsSort} columnKey="contract">Contract</SortableHeaderCell>
                    <SortableHeaderCell sort={receiptsSort} columnKey="investor">Investor</SortableHeaderCell>
                    <SortableHeaderCell sort={receiptsSort} columnKey="payment_date">Payment Date</SortableHeaderCell>
                    <SortableHeaderCell sort={receiptsSort} columnKey="amount" align="right">Amount</SortableHeaderCell>
                    <SortableHeaderCell sort={receiptsSort} columnKey="matched_date">Matched Date</SortableHeaderCell>
                    <SortableHeaderCell sort={receiptsSort} columnKey="reference">Reference</SortableHeaderCell>
                    <TableCell>Proof</TableCell>
                    <SortableHeaderCell sort={receiptsSort} columnKey="status">Status</SortableHeaderCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {receipts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        <Box sx={{ py: 3 }}>
                          <ReceiptIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                          <Typography color="textSecondary" fontWeight="medium">
                            No receipts recorded yet
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Capital receipts will appear here once recorded
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    groupByContract(receiptsSort.sortRows(fReceipts, RECEIPT_COLUMNS).slice(receiptPage * rowsPerPage, receiptPage * rowsPerPage + rowsPerPage)).flatMap((group) => {
                    const expanded = isGroupExpanded(group);
                    const header = renderGroupHeader(group, 9, expanded);
                    if (!expanded) return [header];
                    return [header, ...group.rows.map((receipt) => (
                      <TableRow
                        key={receipt.id}
                        hover
                        onClick={() => handleOpenViewDialog(receipt, 'capital-receipt')}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          <ContractReferenceCell reference={receipt.contract_reference || '-'} userReference={receipt.user_reference} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{receipt.investor_name}</Typography>
                        </TableCell>
                        <TableCell>
                          {format(new Date(receipt.received_date), 'dd MMM yyyy')}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="medium">
                            {receipt.currency} {formatNumber(receipt.amount)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {receipt.created_at ? format(new Date(receipt.created_at), 'dd MMM yyyy') : '-'}
                        </TableCell>
                        <TableCell>{receipt.reference || '-'}</TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          {receipt.proof_document_id ? (
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Tooltip title="Preview Document">
                                <IconButton
                                  size="small"
                                  onClick={() => docActions.openPreview(receipt.proof_document_id)}
                                >
                                  <PreviewIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Download Proof">
                                <IconButton
                                  size="small"
                                  onClick={() => docActions.downloadDocument(receipt.proof_document_id)}
                                >
                                  <DownloadIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          ) : (
                            <Typography variant="caption" color="textSecondary">-</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label="Matched"
                            size="small"
                            variant="outlined"
                            color="success"
                          />
                        </TableCell>
                        <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                          <Tooltip title="Unmatch receipt">
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => handleDeleteReceipt(receipt.id)}
                            >
                              <UnmatchIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))];
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {receipts.length > rowsPerPage && (
              <TablePagination
                component="div"
                count={receipts.length}
                page={receiptPage}
                onPageChange={(_, newPage) => setReceiptPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setReceiptPage(0);
                }}
              />
            )}
          </Paper>
        </TabPanel>
        </>
        )}
        {capitalDirection === 'out' && (
        <>
          <Paper sx={{ mb: 0 }}>
            <Tabs value={capitalOutSubTab} onChange={(_, v) => setCapitalOutSubTab(v)}>
              <Tab label={`Overdue (${fCapitalOutOverdue.length})`} />
              <Tab label={`Upcoming (${fCapitalOutUpcoming.length})`} />
            </Tabs>
          </Paper>

          <TabPanel value={capitalOutSubTab} index={0}>
            <OverduePaymentsTab
              payments={fCapitalOutOverdue.map(p => ({
                ...p,
                payment_date: p.payment_date,
                payment_amount: p.scheduled_amount,
                currency_code: p.currency,
              }))}
              showContractColumn={true}
              showInvestorColumn={true}
              showMatchActions={true}
              onMatch={handleOpenMatchDialog}
            />
          </TabPanel>

          <TabPanel value={capitalOutSubTab} index={1}>
            <UpcomingPaymentsTab
              payments={fCapitalOutUpcoming.map(p => ({
                ...p,
                payment_date: p.payment_date,
                payment_amount: p.scheduled_amount,
                currency_code: p.currency,
              }))}
              showContractColumn={true}
              showInvestorColumn={true}
              showMatchActions={true}
              onMatch={handleOpenMatchDialog}
            />
          </TabPanel>
        </>
        )}
      </Box>
      )}
      {/* ===== Withholding Tax Tab ===== */}
      {activePaymentFeature === 'withholding_tax' && (
      <Box>
        <Paper sx={{ mb: 0 }}>
          <Tabs value={whtSubTab} onChange={(_, v) => setWhtSubTab(v)}>
            <Tab label={`Pending Withholding Tax (${fPendingWht.length})`} />
            <Tab label={`Matched Withholding Tax (${fMatchedWht.length})`} />
          </Tabs>
        </Paper>

        {/* Pending WHT Payments Sub-Tab */}
        <TabPanel value={whtSubTab} index={0}>
          <Paper>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <SortableHeaderCell sort={pendingWhtSort} columnKey="contract">Contract</SortableHeaderCell>
                    <SortableHeaderCell sort={pendingWhtSort} columnKey="investor">Investor</SortableHeaderCell>
                    <SortableHeaderCell sort={pendingWhtSort} columnKey="interest_date">Interest Date</SortableHeaderCell>
                    <SortableHeaderCell sort={pendingWhtSort} columnKey="gross_interest" align="right">Gross Interest</SortableHeaderCell>
                    <SortableHeaderCell sort={pendingWhtSort} columnKey="wht_rate" align="right">WHT Rate</SortableHeaderCell>
                    <SortableHeaderCell sort={pendingWhtSort} columnKey="wht_due" align="right">WHT Due</SortableHeaderCell>
                    <SortableHeaderCell sort={pendingWhtSort} columnKey="days_overdue">Status</SortableHeaderCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingWht.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Box sx={{ py: 3 }}>
                          <MatchIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
                          <Typography color="success.main" fontWeight="medium">
                            No pending withholding tax payments
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            All withholding tax payments are up to date
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    pendingWhtSort.sortRows(fPendingWht, PENDING_WHT_COLUMNS).slice(whtPage * rowsPerPage, whtPage * rowsPerPage + rowsPerPage).map((entry) => (
                      <TableRow
                        key={entry.schedule_id}
                        hover
                        onClick={() => handleOpenWhtMatchDialog(entry)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          <ContractReferenceCell reference={entry.contract_reference} userReference={entry.user_reference} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{entry.investor_name}</Typography>
                        </TableCell>
                        <TableCell>
                          {format(new Date(entry.payment_date), 'dd MMM yyyy')}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {entry.currency_code} {formatNumber(entry.gross_interest_amount)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {parseFloat(entry.wht_rate_applied || 0).toFixed(2)}%
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="medium">
                            {entry.currency_code} {formatNumber(entry.wht_amount)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {entry.days_overdue > 0 ? (
                            <Chip
                              label={`${entry.days_overdue} days overdue`}
                              size="small"
                              variant="outlined"
                              color="error"
                            />
                          ) : (
                            <Chip label="Pending" size="small" variant="outlined" />
                          )}
                        </TableCell>
                        <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleOpenWhtMatchDialog(entry)}
                          >
                            Match
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {pendingWht.length > rowsPerPage && (
              <TablePagination
                component="div"
                count={pendingWht.length}
                page={whtPage}
                onPageChange={(_, newPage) => setWhtPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setWhtPage(0);
                }}
              />
            )}
          </Paper>
        </TabPanel>

        {/* Matched WHT Payments Sub-Tab */}
        <TabPanel value={whtSubTab} index={1}>
          <Paper>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <SortableHeaderCell sort={matchedWhtSort} columnKey="contract">Contract</SortableHeaderCell>
                    <SortableHeaderCell sort={matchedWhtSort} columnKey="investor">Investor</SortableHeaderCell>
                    <SortableHeaderCell sort={matchedWhtSort} columnKey="payment_date">Payment Date</SortableHeaderCell>
                    <SortableHeaderCell sort={matchedWhtSort} columnKey="amount" align="right">Amount</SortableHeaderCell>
                    <SortableHeaderCell sort={matchedWhtSort} columnKey="matched_date">Matched Date</SortableHeaderCell>
                    <SortableHeaderCell sort={matchedWhtSort} columnKey="reference">Reference</SortableHeaderCell>
                    <TableCell>Proof</TableCell>
                    <SortableHeaderCell sort={matchedWhtSort} columnKey="status">Status</SortableHeaderCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {matchedWht.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        <Box sx={{ py: 3 }}>
                          <ReceiptIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                          <Typography color="textSecondary" fontWeight="medium">
                            No matched withholding tax payments yet
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    groupByContract(matchedWhtSort.sortRows(fMatchedWht, MATCHED_WHT_COLUMNS).slice(matchedWhtPage * rowsPerPage, matchedWhtPage * rowsPerPage + rowsPerPage)).flatMap((group) => {
                    const expanded = isGroupExpanded(group);
                    const header = renderGroupHeader(group, 10, expanded);
                    if (!expanded) return [header];
                    return [header, ...group.rows.map((payment) => (
                      <TableRow
                        key={payment.id}
                        hover
                        onClick={() => handleOpenViewDialog(payment, 'wht')}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          <ContractReferenceCell reference={payment.contract_reference} userReference={payment.user_reference} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{payment.investor_name}</Typography>
                        </TableCell>
                        <TableCell>
                          {format(new Date(payment.payment_date), 'dd MMM yyyy')}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="medium">
                            {payment.currency_code} {formatNumber(payment.payment_amount)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {payment.processed_at ? format(new Date(payment.processed_at), 'dd MMM yyyy') : '-'}
                        </TableCell>
                        <TableCell>{payment.payment_reference || '-'}</TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          {payment.proof_of_payment_document ? (
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Tooltip title="Preview Document">
                                <IconButton
                                  size="small"
                                  onClick={() => docActions.openPreview(payment.proof_of_payment_document)}
                                >
                                  <PreviewIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Download Proof">
                                <IconButton
                                  size="small"
                                  onClick={() => docActions.downloadDocument(payment.proof_of_payment_document)}
                                >
                                  <DownloadIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          ) : (
                            <Typography variant="caption" color="textSecondary">-</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={payment.payment_status || 'Matched'}
                            size="small"
                            variant="outlined"
                            color={getStatusColor(payment.payment_status || 'matched')}
                          />
                        </TableCell>
                        <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                          <Tooltip title="Edit apportionment / variance reason">
                            <IconButton size="small" color="primary" onClick={() => handleOpenReapportionDialog(payment)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Unmatch payment">
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => handleUnmatchWht(payment.id)}
                            >
                              <UnmatchIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))];
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {matchedWht.length > rowsPerPage && (
              <TablePagination
                component="div"
                count={matchedWht.length}
                page={matchedWhtPage}
                onPageChange={(_, newPage) => setMatchedWhtPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setMatchedWhtPage(0);
                }}
              />
            )}
          </Paper>
        </TabPanel>
      </Box>
      )}
      {/* WHT Match Dialog — handled by unified MatchDialog (matchType='wht') */}
      {/* Match Payment Dialog — unified MatchDialog handles Payments / Combined / WHT */}
      {/* Record Receipt Dialog */}
      <Dialog open={receiptDialogOpen} onClose={handleCloseReceiptDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Record Capital Receipt</DialogTitle>
        <DialogContent>
          {/* Show contract details when receipting from pending draw down */}
          {selectedDrawDown ? (
            <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'success.50' }}>
              <Typography variant="subtitle2" gutterBottom color="success.dark">
                Receipting Capital Draw Down
              </Typography>
              <Grid container spacing={1}>
                <Grid size={6}>
                  <Typography variant="caption" color="textSecondary">Contract</Typography>
                  <ContractReferenceCell reference={selectedDrawDown.contract_reference} userReference={selectedDrawDown.user_reference} />
                </Grid>
                <Grid size={6}>
                  <Typography variant="caption" color="textSecondary">Investor</Typography>
                  <Typography variant="body2">{selectedDrawDown.investor_name}</Typography>
                </Grid>
                <Grid size={6}>
                  <Typography variant="caption" color="textSecondary">Expected Date</Typography>
                  <Typography variant="body2">
                    {format(new Date(selectedDrawDown.expected_date), 'dd MMM yyyy')}
                  </Typography>
                </Grid>
                <Grid size={6}>
                  <Typography variant="caption" color="textSecondary">Expected Capital</Typography>
                  <Typography variant="body2" fontWeight="medium" color="success.dark">
                    {selectedDrawDown.currency_code} {formatNumber(selectedDrawDown.capital_amount)}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          ) : (
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Manually record capital received from an investor
            </Typography>
          )}

          <form>
            <Grid container spacing={2}>
              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <Controller
                  name="amount"
                  control={receiptControl}
                  rules={{ required: 'Amount is required' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Amount Received"
                      type="number"
                      fullWidth
                      required
                      InputProps={{
                        startAdornment: selectedDrawDown ? (
                          <InputAdornment position="start">
                            {selectedDrawDown.currency_code}
                          </InputAdornment>
                        ) : null,
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <Controller
                  name="received_date"
                  control={receiptControl}
                  rules={{ required: 'Date is required' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Date Received"
                      type="date"
                      fullWidth
                      required
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <Controller
                  name="reference"
                  control={receiptControl}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Payment Reference"
                      fullWidth
                      placeholder="Bank reference"
                    />
                  )}
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <FormControl fullWidth>
                  <InputLabel>Payment Method</InputLabel>
                  <Controller
                    name="payment_method"
                    control={receiptControl}
                    render={({ field }) => (
                      <Select {...field} label="Payment Method">
                        <MenuItem value="EFT">EFT</MenuItem>
                        <MenuItem value="Wire Transfer">Wire Transfer</MenuItem>
                        <MenuItem value="Check">Check</MenuItem>
                        <MenuItem value="Cash">Cash</MenuItem>
                        <MenuItem value="Other">Other</MenuItem>
                      </Select>
                    )}
                  />
                </FormControl>
              </Grid>

              <Grid size={12}>
                <Controller
                  name="notes"
                  control={receiptControl}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Notes"
                      fullWidth
                      multiline
                      rows={2}
                      placeholder="Additional notes"
                    />
                  )}
                />
              </Grid>

              {/* Proof of Receipt Upload — label switches between Required and Optional */}
              <Grid size={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Proof of Receipt {isWorkflowDocumentRequired('capital_receipt') ? '(Required)' : '(Optional)'}
                </Typography>
                <Box
                  sx={{
                    border: '2px dashed',
                    borderColor: receiptFile ? 'success.main' : 'divider',
                    borderRadius: 1,
                    p: 2,
                    textAlign: 'center',
                    cursor: 'pointer',
                    '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
                  }}
                  onClick={() => document.getElementById('receipt-file-input').click()}
                >
                  <input
                    id="receipt-file-input"
                    type="file"
                    hidden
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    onChange={(e) => setReceiptFile(e.target.files[0])}
                  />
                  {receiptFile ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                      <DescriptionIcon color="success" />
                      <Typography>{receiptFile.name}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        ({formatFileSize(receiptFile.size)})
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                      <CloudUploadIcon color="action" />
                      <Typography color="textSecondary">Click to upload proof of receipt (bank statement, etc.)</Typography>
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseReceiptDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleReceiptSubmit(handleCreateReceipt)}
            startIcon={uploadingReceipt ? <CircularProgress size={20} /> : <ReceiptIcon />}
            disabled={uploadingReceipt || (isWorkflowDocumentRequired('capital_receipt') && !receiptFile)}
          >
            Record Receipt
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText="Confirm"
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
      />
      {/* Document Preview Dialog */}
      <DocumentPreviewDialog {...docActions} />
      <MatchDialog
        open={matchDialogOpenUnified}
        onClose={handleCloseUnifiedMatch}
        onSubmit={handleUnifiedMatchSubmit}
        submitting={matchDialogSubmitting}
        matchType={matchDialogType}
        scheduleRow={matchDialogRow}
        currencyCode={matchDialogRow?.currency || matchDialogRow?.currency_code || ''}
        contractReference={matchDialogRow?.contract_reference}
        userReference={matchDialogRow?.user_reference}
        investorName={matchDialogRow?.investor_name}
        whtContext={matchDialogRow?._wht_context}
        mode={matchDialogMode}
        initialPayment={matchDialogInitialPayment}
        initialAllocation={matchDialogInitialAllocation}
        initialVarianceReason={matchDialogInitialReason}
      />
    </Box>
  );
}

export default PaymentMatching;
